import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, FolderOpen, User, Clock, Building2, MessageSquare, AlertTriangle, Send, Plus, X, Loader2, ChevronDown, ChevronUp,
  Mail, Link2, Calendar, Tag, Shield, Users, FileText, CheckCircle2, Clock3, Flame, History, Paperclip, AtSign, RefreshCw, ArrowUpRight, Reply, UserCheck, ArrowRight,
  Sparkles, FileSpreadsheet, Copy, Download, Bot, Check
} from 'lucide-react'
import { Card, Badge, StatusIndicator, Button, cn } from '../atoms'
import { AttachmentPicker } from '../molecules/AttachmentPicker'
import { EmailAutocomplete } from '../molecules/EmailAutocomplete'
import { QuotedTextCollapsible } from '../molecules/QuotedTextCollapsible'
import { CaseTasksSection } from '../organisms/CaseTasksSection'
import type { UploadedAttachment } from '../../hooks/useUploadAttachment'
import { getAssetUrl, getBackendUrl, rewriteSignatureImages } from '../../utils/assetUrl'
import { useCase, useCaseReferences, useUpdateCaseStatus, useAddCommunication, useAssignCase, useGenerateQuickResponse, useEscalateCase, useCaseEscalations, useGenerateCaseResponse, useAnalyzeCaseHistory } from '../../hooks/useCases'

import { useReplySignature, useMySignature } from '../../hooks/useEmails'
import { useAreas } from '../../hooks/useAreas'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { formatEmailDescription, isHtml } from '../../utils/emailParser'
import { useImpersonation } from '../../store/impersonation.store'
import { ErrorBoundary } from '../common/ErrorBoundary'

const criticidadConfig: Record<string, { color: string; bg: string; label: string; icon: any }> = {
  CM:             { color: 'text-white', bg: 'bg-red-600 border-red-600', label: 'Crítico Máximo', icon: Flame },
  CRITICO_MAXIMO: { color: 'text-white', bg: 'bg-red-600 border-red-600', label: 'Crítico Máximo', icon: Flame },
  C:              { color: 'text-red-700', bg: 'bg-red-100 border-red-300', label: 'Crítico', icon: AlertTriangle },
  CRITICO:        { color: 'text-red-700', bg: 'bg-red-100 border-red-300', label: 'Crítico', icon: AlertTriangle },
  critical:       { color: 'text-red-700', bg: 'bg-red-100 border-red-300', label: 'Crítico', icon: AlertTriangle },
  A:              { color: 'text-amber-700', bg: 'bg-amber-100 border-amber-300', label: 'Alto', icon: Clock3 },
  ALTO:           { color: 'text-amber-700', bg: 'bg-amber-100 border-amber-300', label: 'Alto', icon: Clock3 },
  high:           { color: 'text-amber-700', bg: 'bg-amber-100 border-amber-300', label: 'Alto', icon: Clock3 },
  M:              { color: 'text-blue-700', bg: 'bg-blue-100 border-blue-300', label: 'Medio', icon: Clock3 },
  MEDIO:          { color: 'text-blue-700', bg: 'bg-blue-100 border-blue-300', label: 'Medio', icon: Clock3 },
  medium:         { color: 'text-blue-700', bg: 'bg-blue-100 border-blue-300', label: 'Medio', icon: Clock3 },
  B:              { color: 'text-green-700', bg: 'bg-green-100 border-green-300', label: 'Bajo', icon: CheckCircle2 },
  BAJO:           { color: 'text-green-700', bg: 'bg-green-100 border-green-300', label: 'Bajo', icon: CheckCircle2 },
  low:            { color: 'text-green-700', bg: 'bg-green-100 border-green-300', label: 'Bajo', icon: CheckCircle2 },
  INF:            { color: 'text-gray-600', bg: 'bg-gray-100 border-gray-300', label: 'Informativo', icon: CheckCircle2 },
  INFORMATIVO:    { color: 'text-gray-600', bg: 'bg-gray-100 border-gray-300', label: 'Informativo', icon: CheckCircle2 },
}

const riskConfig: Record<string, { color: string; bg: string; label: string }> = {
  critical: { color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200', label: 'Crítico' },
  high:     { color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', label: 'Alto' },
  medium:   { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Medio' },
  low:      { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', label: 'Bajo' },
}

const QUICK_RESPONSES = [
  { id: 'received', label: 'Acuse de recibo', icon: CheckCircle2 },
  { id: 'more_info', label: 'Solicitar información', icon: MessageSquare },
  { id: 'in_review', label: 'En revisión', icon: Clock3 },
  { id: 'resolved', label: 'Caso resuelto', icon: CheckCircle2 },
  { id: 'internal_note', label: 'Nota interna', icon: FileText },
]

function slaStatus(date: string | undefined): { color: string; bg: string; label: string; icon: any } | null {
  if (!date) return null
  const now = new Date().getTime()
  const d = new Date(date).getTime()
  if (isNaN(d)) return null
  if (d < now) return { color: 'text-rose-700', bg: 'bg-rose-50', label: 'Vencido', icon: Flame }
  if (d < now + 4 * 60 * 60 * 1000) return { color: 'text-amber-700', bg: 'bg-amber-50', label: 'Por vencer', icon: Clock3 }
  return { color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'En plazo', icon: CheckCircle2 }
}

function timeAgo(dateStr: string | undefined | null): string {
  if (!dateStr) return '-'
  const dateObj = new Date(dateStr)
  const then = dateObj.getTime()
  if (isNaN(then)) return '-'
  const now = new Date().getTime()
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'ahora'
  if (diffMins < 60) return `hace ${diffMins} min`
  if (diffHours < 24) return `hace ${diffHours}h`
  if (diffDays < 7) return `hace ${diffDays}d`
  return dateObj.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
}

export function CaseDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: caseData, isLoading, isError, error, refetch } = useCase(id!)
  const updateStatus = useUpdateCaseStatus()
  const addComm = useAddCommunication()
  const assignCase = useAssignCase()
  const generateQuickResponse = useGenerateQuickResponse()
  const escalateCase = useEscalateCase()
  const { data: escalationHistory } = useCaseEscalations(id!)
  const { data: references } = useCaseReferences()
  const { data: areas } = useAreas()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()

  const generateCaseResponseMutation = useGenerateCaseResponse()
  const analyzeCaseHistoryMutation = useAnalyzeCaseHistory()

  const [showCommForm, setShowCommForm] = useState(false)
  const [commType, setCommType] = useState<'internal' | 'reply_all' | 'reply' | 'forward'>('reply')
  const [commBody, setCommBody] = useState('')
  const [commSubject, setCommSubject] = useState('')
  const [commAttachments, setCommAttachments] = useState<UploadedAttachment[]>([])
  const [commRecipients, setCommRecipients] = useState('')
  const [commCc, setCommCc] = useState('')
  const [aiTone, setAiTone] = useState<'formal' | 'friendly' | 'empathetic'>('formal')
  const [aiCustomInstructions, setAiCustomInstructions] = useState('')
  const [showAiInstructions, setShowAiInstructions] = useState(false)
  const [suggestedCaseResponse, setSuggestedCaseResponse] = useState('')
  
  // Modal de análisis de historial
  const [showAnalysisModal, setShowAnalysisModal] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<any>(null)
  const [copiedReport, setCopiedReport] = useState(false)

  const [showEscalateModal, setShowEscalateModal] = useState(false)
  const [escalateReason, setEscalateReason] = useState('')
  const [escalateLevel, setEscalateLevel] = useState(1)
  const [escalateCategory, setEscalateCategory] = useState('operational')
  const [escalateNotifyRoles, setEscalateNotifyRoles] = useState<string[]>(['MANAGER'])
  const [escalateAreaId, setEscalateAreaId] = useState<string>('')
  const [showQuickResponses, setShowQuickResponses] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null)
  const [selectedCoAssignees, setSelectedCoAssignees] = useState<Set<string>>(new Set())
  const [generatingQuickResponse, setGeneratingQuickResponse] = useState(false)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showAlertsModal, setShowAlertsModal] = useState(false)
  const [showEmailsModal, setShowEmailsModal] = useState(false)
  const [showCoAssigneesModal, setShowCoAssigneesModal] = useState(false)
  const [showDescription, setShowDescription] = useState(false)
  const { impersonateMailboxId } = useImpersonation()


  const relatedEmailId = (caseData as any)?.emails?.[0]?.id || ''
  const emailSignatureQuery = useReplySignature(relatedEmailId, showCommForm && commType !== 'internal' && !impersonateMailboxId)
  const mySignatureQuery = useMySignature(impersonateMailboxId, showCommForm && commType !== 'internal')
  const replySignatureQuery = impersonateMailboxId ? mySignatureQuery : (relatedEmailId ? emailSignatureQuery : mySignatureQuery)

  const c = (caseData as any) || {}
  const communicationList: any[] = useMemo(() => {
    if (!caseData) return []
    const comms = (c.communications || []).map((comm: any) => ({
      ...comm,
      _itemKind: 'COMMUNICATION',
      _eventTime: new Date(comm.sentAt || comm.createdAt || comm.date || 0).getTime()
    }))
    const states = (c.states || []).map((st: any) => ({
      ...st,
      _itemKind: 'STATE_EVENT',
      _eventTime: new Date(st.createdAt || 0).getTime()
    }))
    return [...comms, ...states].sort((a: any, b: any) => (a._eventTime || 0) - (b._eventTime || 0))
  }, [c.communications, c.states, caseData])

  // Estado para desplegar/plegar items del historial
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})
  const [allExpanded, setAllExpanded] = useState<boolean | null>(null)

  // Encontrar el item más relevante del historial (el mensaje/comunicación más reciente)
  const mostRelevantKey = useMemo(() => {
    if (!communicationList || communicationList.length === 0) return null
    for (let i = communicationList.length - 1; i >= 0; i--) {
      const item = communicationList[i]
      if (item._itemKind === 'COMMUNICATION' || item.type) {
        return item.id ? String(item.id) : `comm-${i}`
      }
    }
    const last = communicationList[communicationList.length - 1]
    return last.id ? String(last.id) : `item-${communicationList.length - 1}`
  }, [communicationList])

  const isItemExpanded = (key: string) => {
    if (allExpanded === true) return true
    if (allExpanded === false) return false
    if (expandedItems[key] !== undefined) return expandedItems[key]
    return key === mostRelevantKey
  }

  const toggleItemExpanded = (key: string) => {
    const currentlyExpanded = isItemExpanded(key)
    setExpandedItems(prev => ({ ...prev, [key]: !currentlyExpanded }))
    if (allExpanded !== null) setAllExpanded(null)
  }

  const handleOpenAssignModal = () => {
    const caseObj = caseData as any
    setSelectedAssignee(caseObj?.assignee?.id || null)
    const coIds = new Set<string>(caseObj?.coAssignees?.map((ca: any) => ca.id) || [])
    setSelectedCoAssignees(coIds)
    setShowAssignModal(true)
  }

  const openCommunicationForm = (type: 'reply' | 'reply_all' | 'forward' | 'internal') => {
    setCommType(type)
    const relatedEmail = c.emails?.[c.emails.length - 1] || c.emails?.[0]
    const origFrom = relatedEmail?.from || c.metadata?.originalFrom || (c.communications?.find((cm: any) => cm.recipients?.length)?.recipients?.[0]) || ''
    
    if (type === 'internal') {
      setCommRecipients('')
      setCommCc('')
      setCommSubject(c.subject ? `Nota interna: ${c.subject}` : 'Nota interna')
    } else if (type === 'reply') {
      setCommRecipients(origFrom)
      setCommCc('')
      const subj = relatedEmail?.subject || c.subject || ''
      setCommSubject(subj.match(/^re:/i) ? subj : `Re: ${subj}`)
    } else if (type === 'reply_all') {
      setCommRecipients(origFrom)
      setCommCc((relatedEmail?.cc || []).join(', '))
      const subj = relatedEmail?.subject || c.subject || ''
      setCommSubject(subj.match(/^re:/i) ? subj : `Re: ${subj}`)
    } else if (type === 'forward') {
      setCommRecipients('')
      setCommCc('')
      const subj = relatedEmail?.subject || c.subject || ''
      setCommSubject(subj.match(/^fwd?:/i) ? subj : `Fwd: ${subj}`)
    }
    setShowCommForm(true)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <p className="text-sm text-gray-500">Cargando caso...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-12 max-w-md mx-auto bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-8 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Error al cargar el caso</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {(error as any)?.message || 'No fue posible obtener los datos del caso desde el servidor.'}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="primary" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Reintentar
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/cases')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Volver a casos
          </Button>
        </div>
      </div>
    )
  }

  if (!caseData) {
    return (
      <div className="text-center py-12 max-w-md mx-auto bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-8 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mx-auto mb-4">
          <FolderOpen className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Caso no encontrado</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">El caso solicitado no existe o no tiene permisos para visualizarlo.</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/cases')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Volver a casos
        </Button>
      </div>
    )
  }
  const isClosed = c.status?.name === 'closed'
  const isEscalated = (c.escalations?.length ?? 0) > 0

  const rawCritKey = c.criticidad || c.emails?.[0]?.criticidad || c.riskLevel?.name || ''
  const critKey = String(rawCritKey).trim()
  const criticidad = criticidadConfig[critKey] || criticidadConfig[critKey.toUpperCase()] || criticidadConfig.INF
  const CriticidadIcon = criticidad.icon

  const handleEscalate = async () => {
    try {
      await escalateCase.mutateAsync({
        caseId: id!,
        level: escalateLevel,
        category: escalateCategory,
        reason: escalateReason || 'Escalado por el operador',
        notifyRoles: escalateNotifyRoles,
        areaId: escalateAreaId || undefined
      })
      successToast(`Caso escalado a Nivel ${escalateLevel}`)
      setShowEscalateModal(false)
      setEscalateReason('')
      setEscalateLevel(1)
      setEscalateCategory('operational')
      setEscalateAreaId('')
    } catch { errorToast('Error al escalar el caso') }
  }

  const handleAssign = async () => {
    if (!selectedAssignee) return
    try {
      await assignCase.mutateAsync({
        id: id!,
        assignedTo: selectedAssignee,
        coAssignees: Array.from(selectedCoAssignees)
      })
      successToast('Caso asignado correctamente')
      setShowAssignModal(false)
    } catch { errorToast('Error al asignar el caso') }
  }

  const handleAddCommunication = async () => {
    if (!commBody.trim()) return
    const recipientsList = commRecipients
      ? commRecipients.split(/[,;]+/).map(e => e.trim()).filter(Boolean)
      : []
    const ccList = commCc
      ? commCc.split(/[,;]+/).map(e => e.trim()).filter(Boolean)
      : []
    const allRecipients = [...recipientsList, ...ccList]
    try {
      await addComm.mutateAsync({
        caseId: id!,
        data: {
          type: commType === 'reply_all' ? 'outbound' : commType === 'reply' ? 'outbound' : commType === 'forward' ? 'outbound' : 'internal',
          subject: commSubject || (commType === 'internal' ? 'Nota interna' : commType === 'reply_all' ? 'Respuesta a todos' : commType === 'reply' ? 'Respuesta' : 'Reenvío'),
          body: commBody,
          htmlBody: commType !== 'internal' ? `<p>${commBody.replace(/\n/g, '<br>')}</p>` : undefined,
          recipients: recipientsList.length > 0 ? recipientsList : undefined,
          cc: ccList.length > 0 ? ccList : undefined,
          emailId: relatedEmailId || undefined,
          attachments: commAttachments.map(a => ({
            id: a.id, url: a.url, filename: a.filename, mimeType: a.mimeType, size: a.size, localPath: a.localPath,
          })),
        },
      })
      successToast('Comunicación agregada')
      setCommBody('')
      setCommSubject('')
      setCommAttachments([])
      setCommRecipients('')
      setCommCc('')
      setShowCommForm(false)
      setShowQuickResponses(false)
      // Auto-cambiar a "En gestión" si el caso estaba cerrado
      if (isClosed) {
        await updateStatus.mutateAsync({ id: id!, statusName: 'in_progress' })
      }
    } catch { errorToast('Error al agregar comunicación') }
  }

  const selectQuickResponse = async (template: typeof QUICK_RESPONSES[0]) => {
    setGeneratingQuickResponse(true)
    const relatedEmail = c.emails?.[0]
    if (relatedEmail) {
      try {
        const res: any = await generateQuickResponse.mutateAsync({
          emailId: relatedEmail.id,
          type: template.id as any,
          tone: 'formal'
        })
        setCommBody(res?.body || `\n\n[${template.label} - generado por IA]`)
        setCommSubject(template.label)
        if (template.id === 'internal_note') setCommType('internal')
        setShowQuickResponses(false)
        setShowCommForm(true)
        setGeneratingQuickResponse(false)
        return
      } catch (err: any) {
        errorToast(err?.response?.data?.message || 'Error generando respuesta con IA')
      }
    }
    setCommBody(`[${template.label}]\n\n`)
    setCommSubject(template.label)
    if (template.id === 'internal_note') setCommType('internal')
    setShowQuickResponses(false)
    setShowCommForm(true)
    setGeneratingQuickResponse(false)
  }

  const handleStatusChange = async (statusName: string) => {
    if (['closed', 'resolved'].includes(statusName)) {
      const caseTasks = Array.isArray(c?.tasks) ? c.tasks : []
      const pending = caseTasks.filter((t: any) => !t.isCompleted)
      if (pending.length > 0) {
        const confirmClose = window.confirm(
          `Atención: Este caso tiene ${pending.length} tarea(s) operativa(s) pendiente(s):\n\n` +
          pending.map((t: any) => `• ${t.description}`).join('\n') +
          `\n\n¿Deseas cerrar el caso de todas formas?`
        )
        if (!confirmClose) return
      }
    }
    try {
      await updateStatus.mutateAsync({ id: id!, statusName })
      successToast(`Estado actualizado a ${statusName}`)
      setShowStatusDropdown(false)
    } catch { errorToast('Error al actualizar estado') }
  }

  return (
    <ErrorBoundary title="Error en detalle de caso" message="Ocurrió un problema al mostrar el detalle de este caso.">
      <div className="space-y-5">
        {/* BARRA SUPERIOR: Navegación */}
        <button onClick={() => navigate('/cases')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Volver a casos
        </button>

      {/* CARD PRINCIPAL: Título + descripción */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 md:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Izquierda: Info del caso */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-gray-100 border border-gray-200">
                  <FolderOpen className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">{c.caseNumber}</span>
                </div>
                <StatusIndicator status={(c.status?.name || c.status) as any} />
                {((escalationHistory as any[])?.length || 0) > 0 && (
                  <Badge variant="danger" className="text-[10px] px-2 py-0.5">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Escalado N{(escalationHistory as any[])[0].level}
                  </Badge>
                )}
              </div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">{c.subject}</h1>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold', criticidad.bg, criticidad.color)}>
                  <CriticidadIcon className="w-3.5 h-3.5" />
                  {criticidad.label}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(c.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
                </div>
                {c.area?.name && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md font-medium">
                    <span>Área: {c.area.name}</span>
                  </div>
                )}
                {/* Respuesta SLA */}
                {c.slaResponseDue && (
                  <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium', slaStatus(c.slaResponseDue)?.bg || 'bg-gray-50', slaStatus(c.slaResponseDue)?.color || 'text-gray-700')}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>Respuesta: <strong>{new Date(c.slaResponseDue).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong> ({slaStatus(c.slaResponseDue)?.label})</span>
                  </div>
                )}
                {/* Resolución SLA */}
                {c.slaResolutionDue && (
                  <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium', slaStatus(c.slaResolutionDue)?.bg || 'bg-gray-50', slaStatus(c.slaResolutionDue)?.color || 'text-gray-700')}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>Resolución: <strong>{new Date(c.slaResolutionDue).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong> ({slaStatus(c.slaResolutionDue)?.label})</span>
                  </div>
                )}
              </div>
            </div>

            {/* Derecha: Acción de estado */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative">
                {isClosed ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('in_progress')}
                    className="border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Reabrir
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      className="border-gray-300"
                    >
                      <Tag className="w-4 h-4 mr-1.5" />
                      {c.status?.label || c.status?.name || 'Estado'}
                    </Button>
                    {showStatusDropdown && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                        <div className="absolute right-0 z-20 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                          {[
                            { key: 'new', label: 'Nuevo', color: 'bg-blue-500' },
                            { key: 'in_progress', label: 'En gestión', color: 'bg-blue-500' },
                            { key: 'closed', label: 'Cerrado', color: 'bg-gray-500' },
                          ].map((s) => (
                            <button
                              key={s.key}
                              onClick={() => handleStatusChange(s.key)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <div className={cn('w-2 h-2 rounded-full', s.color)} />
                                {s.label}
                                {c.status?.name === s.key && <CheckCircle2 className="w-3.5 h-3.5 ml-auto text-primary-600" />}
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Descripción integrada como desplegable */}
        {c.description && (
          <div className="border-t border-gray-100 bg-gray-50/40">
            <button
              onClick={() => setShowDescription(!showDescription)}
              className="w-full flex items-center justify-between px-5 md:px-6 py-3 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                <span>Descripción del caso</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gray-400 font-normal">
                <span>{showDescription ? 'Ocultar descripción' : 'Ver descripción'}</span>
                <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform duration-200", showDescription && "rotate-180")} />
              </div>
            </button>
            {showDescription && (
              <div className="px-5 md:px-6 pb-5 pt-1">
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-400 to-primary-200 rounded-full" />
                  <div
                    className="email-content text-sm text-gray-700 leading-relaxed pl-4 max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: formatEmailDescription(c.description) }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tareas Operativas del Caso (Checklist Interactivo) */}
      <CaseTasksSection
        caseId={c.id}
        tasks={c.tasks || []}
        canEdit={true}
      />

      {/* Timeline de comunicaciones - ancho completo */}
      <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Historial de actividad</h3>
                <span className="text-xs text-gray-400">({communicationList.length})</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Botón Asignar */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenAssignModal}
                  className="border-gray-300 hover:bg-gray-50"
                >
                  <Users className="w-4 h-4 mr-1.5 text-primary-600" />
                  Asignar{((c.coAssignees?.length ?? 0) > 0 || c.assignee) ? ` (${[c.assignee?.firstName || c.assignedTo, ...((c.coAssignees || []).map((ca: any) => ca.firstName))].filter(Boolean).length})` : ''}
                </Button>
                {/* Botón Escalar */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEscalateModal(true)}
                  className={((escalationHistory as any[])?.length || 0) > 0 ? 'border-amber-300 text-amber-700 bg-amber-50' : 'border-gray-300'}
                >
                  <AlertTriangle className="w-4 h-4 mr-1.5" />
                  {((escalationHistory as any[])?.length || 0) > 0 ? `Escalado N${(escalationHistory as any[])[0].level}` : 'Escalar'}
                </Button>
                {/* Botón Alertas */}
                {(c.alerts?.length ?? 0) > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setShowAlertsModal(true)} className="border-red-200 text-red-600 hover:bg-red-50">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Alertas ({c.alerts.length})
                  </Button>
                )}
                {/* Botón Emails */}
                {(c.emails?.length ?? 0) > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setShowEmailsModal(true)} className="border-blue-200 text-blue-600 hover:bg-blue-50">
                    <Mail className="w-4 h-4 mr-1.5" />
                    Emails ({c.emails.length})
                  </Button>
                )}
                {/* Botón Analizar Historial con IA */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setShowAnalysisModal(true)
                    if (!analysisResult) {
                      try {
                        const res = await analyzeCaseHistoryMutation.mutateAsync({ caseId: id! })
                        setAnalysisResult(res)
                      } catch (err: any) {
                        errorToast(err?.message || 'Error al analizar el historial con IA')
                      }
                    }
                  }}
                  className="border-purple-200 text-purple-700 bg-purple-50/70 hover:bg-purple-100 hover:border-purple-300 font-medium"
                >
                  <Sparkles className="w-4 h-4 mr-1.5 text-purple-600 animate-pulse" />
                  Analizar historial (IA)
                </Button>

                {/* Botón Plegar / Desplegar Todo */}
                {communicationList.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (allExpanded === true) {
                        setAllExpanded(false)
                        setExpandedItems({})
                      } else {
                        setAllExpanded(true)
                      }
                    }}
                    className="border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    title={allExpanded === true ? "Plegar todos los mensajes" : "Desplegar todos los mensajes"}
                  >
                    {allExpanded === true ? (
                      <>
                        <ChevronUp className="w-3.5 h-3.5 mr-1" />
                        Plegar todo
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3.5 h-3.5 mr-1" />
                        Desplegar todo ({communicationList.length})
                      </>
                    )}
                  </Button>
                )}

                {!showCommForm && (
                  <Button variant="outline" size="sm" onClick={() => openCommunicationForm('reply')}>
                    <Reply className="w-4 h-4 mr-1.5" />
                    Agregar respuesta
                  </Button>
                )}
              </div>
            </div>

            <div className="p-5">
              {/* Formulario de comunicación */}
              {showCommForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-800">Nueva comunicación</h4>
                    <button onClick={() => { setShowCommForm(false); setCommBody(''); setCommRecipients(''); setCommCc(''); setSuggestedCaseResponse('') }} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-200 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {([
                      { key: 'reply_all', label: 'Responder a todos', desc: 'Responde con copia a todos', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Mail },
                      { key: 'reply', label: 'Responder', desc: 'Respuesta directa', color: 'bg-primary-50 text-primary-700 border-primary-200', icon: Send },
                      { key: 'forward', label: 'Reenviar', desc: 'Reenviar contenido', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: ArrowUpRight },
                      { key: 'internal', label: 'Nota interna', desc: 'Nota privada del equipo', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: FileText },
                    ] as const).map((t) => {
                      const TplIcon = t.icon
                      return (
                        <button key={t.key} onClick={() => openCommunicationForm(t.key as any)}
                          className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all',
                            commType === t.key ? t.color : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                          )}>
                          <TplIcon className="w-3.5 h-3.5" />
                          {t.label}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400 -mt-1">
                    {commType === 'reply_all' && 'Responde al remitente con copia a todos los destinatarios originales'}
                    {commType === 'reply' && 'Respuesta directa al remitente del email'}
                    {commType === 'forward' && 'Reenvía el contenido del email a otros destinatarios'}
                    {commType === 'internal' && 'Nota visible solo para el equipo interno, no se envía email'}
                  </p>
                  {commType !== 'internal' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Para (destinatarios)</label>
                        <EmailAutocomplete
                          value={commRecipients}
                          onChange={setCommRecipients}
                          placeholder="email@ejemplo.com (separar con coma)"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">CC (copia)</label>
                        <EmailAutocomplete
                          value={commCc}
                          onChange={setCommCc}
                          placeholder="email@ejemplo.com (separar con coma)"
                          className="w-full"
                        />
                      </div>
                    </>
                  )}
                  <input type="text" placeholder="Asunto" value={commSubject} onChange={(e) => setCommSubject(e.target.value)}
                    className="block w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />

                  {/* ═══════════ BARRA DE ASISTENTE IA PARA RESPUESTAS EN CASO ═══════════ */}
                  <div className="p-3 bg-gradient-to-r from-purple-50/80 via-indigo-50/60 to-purple-50/80 rounded-xl border border-purple-200/80 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="text-xs font-bold text-purple-900">Asistente IA</span>
                        <select
                          value={aiTone}
                          onChange={(e) => setAiTone(e.target.value as any)}
                          className="text-xs border border-purple-200 rounded-lg px-2.5 py-1 bg-white text-gray-700 focus:ring-2 focus:ring-purple-400 font-medium"
                        >
                          <option value="formal">Tono Formal</option>
                          <option value="friendly">Tono Amable</option>
                          <option value="empathetic">Tono Empático</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowAiInstructions(!showAiInstructions)}
                          className="text-[11px] text-purple-700 hover:text-purple-900 hover:underline font-medium"
                        >
                          {showAiInstructions ? 'Ocultar instrucciones' : '+ Añadir instrucciones'}
                        </button>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        disabled={generateCaseResponseMutation.isPending}
                        onClick={async () => {
                          try {
                            const res = await generateCaseResponseMutation.mutateAsync({
                              caseId: id!,
                              commType,
                              tone: aiTone,
                              customInstructions: aiCustomInstructions,
                              recipient: commRecipients,
                            })
                            if (res?.suggestedResponse) {
                              setSuggestedCaseResponse(res.suggestedResponse)
                              setCommBody(res.suggestedResponse)
                              successToast('Respuesta con IA generada e insertada en el mensaje')
                            }
                          } catch (err: any) {
                            errorToast(err?.message || 'Error generando respuesta con IA')
                          }
                        }}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-sm border-none text-xs px-3 py-1.5"
                      >
                        {generateCaseResponseMutation.isPending ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                            Redactando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                            Sugerir con IA
                          </>
                        )}
                      </Button>
                    </div>

                    {showAiInstructions && (
                      <input
                        type="text"
                        placeholder="Ej: Pedir número de documento, recordar plazo de 3 días, confirmar pago..."
                        value={aiCustomInstructions}
                        onChange={(e) => setAiCustomInstructions(e.target.value)}
                        className="block w-full px-3 py-1.5 text-xs border border-purple-200 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-purple-400"
                      />
                    )}

                    {suggestedCaseResponse && (
                      <div className="flex items-center justify-between text-[11px] text-purple-800 bg-purple-100/70 px-2.5 py-1 rounded-md">
                        <span>✨ Respuesta redactada con IA e insertada. Puedes editarla libremente en el campo inferior.</span>
                        <button
                          type="button"
                          onClick={() => {
                            setCommBody(suggestedCaseResponse)
                            successToast('Texto restablecido a la sugerencia de IA')
                          }}
                          className="font-bold underline hover:text-purple-950 ml-2"
                        >
                          Restablecer sugerencia
                        </button>
                      </div>
                    )}
                  </div>

                  <textarea placeholder={commType === 'internal' ? 'Escriba la nota interna...' : commType === 'forward' ? 'Reenviar el contenido...' : 'Escriba el mensaje...'} rows={5} value={commBody} onChange={(e) => setCommBody(e.target.value)}
                    className="block w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y" />
                  {commType !== 'internal' && replySignatureQuery.data?.html && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                          {replySignatureQuery.data.source === 'gmail' ? 'Firma de Gmail' : 'Firma corporativa'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 [&_img]:max-h-28 [&_img]:w-auto" dangerouslySetInnerHTML={{ __html: rewriteSignatureImages(replySignatureQuery.data.html || '') }} />
                    </div>
                  )}
                  <AttachmentPicker value={commAttachments} onChange={setCommAttachments} disabled={addComm.isPending} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShowCommForm(false); setCommBody(''); setCommSubject(''); setCommAttachments([]); setCommRecipients(''); setCommCc(''); setSuggestedCaseResponse('') }}>Cancelar</Button>
                    <Button size="sm" onClick={handleAddCommunication} disabled={!commBody.trim() || addComm.isPending}>
                      {addComm.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      {commType === 'internal' ? 'Guardar nota' : commType === 'forward' ? 'Reenviar' : commType === 'reply_all' ? 'Enviar a todos' : 'Enviar'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {communicationList.length > 0 ? (
                <div className="space-y-0">
                  {communicationList.map((comm: any, idx: number) => {
                    const isStateEvent = comm._itemKind === 'STATE_EVENT' || (!comm.type && (comm.toStatus || comm.status))
                    const itemKey = comm.id ? String(comm.id) : (isStateEvent ? `state-${idx}` : `comm-${idx}`)
                    const isExpanded = isItemExpanded(itemKey)
                    const isMostRelevant = itemKey === mostRelevantKey

                    if (isStateEvent) {
                      const eventDate = comm.createdAt || comm.date || comm._eventTime
                      const isAssignment = comm.toStatus === 'ASSIGNED' || comm.metadata?.action === 'ASSIGNMENT' || comm.metadata?.type === 'ASSIGNMENT' || (comm.reason && comm.reason.toLowerCase().includes('asignad'))
                      const actorName = comm.changedByUser
                        ? `${comm.changedByUser.firstName} ${comm.changedByUser.lastName}`
                        : comm.metadata?.assignerName || comm.metadata?.assignedByName || (comm.changedBy === 'system' ? 'Sistema' : 'Administrador')
                      const actorEmail = comm.changedByUser?.email

                      // Evento de estado plegado (compacto)
                      if (!isExpanded) {
                        return (
                          <div key={itemKey} className="relative flex gap-4 group">
                            {idx !== communicationList.length - 1 && (
                              <div className="absolute left-[19px] top-8 bottom-0 w-0.5 bg-gray-200" />
                            )}
                            <div className={cn(
                              'relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm',
                              isAssignment ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                            )}>
                              {isAssignment ? <UserCheck className="w-4 h-4" /> : <History className="w-4 h-4" />}
                            </div>
                            <div className={cn('flex-1 pb-2', idx === communicationList.length - 1 ? 'pb-0' : '')}>
                              <div
                                onClick={() => toggleItemExpanded(itemKey)}
                                className="flex items-center justify-between gap-3 p-2.5 rounded-xl border border-gray-200/80 bg-gray-50/70 hover:bg-gray-100/80 hover:border-gray-300 transition-all cursor-pointer text-xs"
                                title="Haz clic para desplegar detalles"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-semibold text-gray-800 truncate">{actorName}</span>
                                  <Badge
                                    variant={isAssignment ? 'info' : 'warning'}
                                    className={cn(
                                      "text-[10px] px-1.5 py-0 flex-shrink-0",
                                      isAssignment && "bg-blue-50 text-blue-700 border-blue-200",
                                      !isAssignment && "bg-amber-50 text-amber-700 border-amber-200"
                                    )}
                                  >
                                    {isAssignment ? 'Asignación' : (comm.status?.label || comm.toStatus || 'Estado')}
                                  </Badge>
                                  <span className="text-gray-500 truncate max-w-sm hidden sm:inline">
                                    {comm.reason || (comm.metadata?.assignedToName ? `Asignado a ${comm.metadata.assignedToName}` : `Estado a ${comm.toStatus}`)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 text-gray-400">
                                  <span className="text-[11px]">{timeAgo(eventDate)}</span>
                                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }

                      // Evento de estado desplegado completo
                      return (
                        <div key={itemKey} className="relative flex gap-4 group">
                          {/* Línea de timeline */}
                          {idx !== communicationList.length - 1 && (
                            <div className="absolute left-[19px] top-10 bottom-0 w-0.5 bg-gray-200" />
                          )}

                          {/* Avatar/Icono */}
                          <div className={cn(
                            'relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm',
                            isAssignment ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                          )}>
                            {isAssignment ? <UserCheck className="w-4 h-4" /> : <History className="w-4 h-4" />}
                          </div>

                          {/* Contenido */}
                          <div className={cn(
                            'flex-1 pb-6',
                            idx === communicationList.length - 1 ? 'pb-0' : ''
                          )}>
                            {/* Header: autor + tipo + fecha */}
                            <div
                              onClick={() => toggleItemExpanded(itemKey)}
                              className="flex items-center gap-2 flex-wrap mb-1 cursor-pointer select-none hover:opacity-80 transition-opacity"
                              title="Haz clic para plegar"
                            >
                              <div className="flex items-center gap-1.5">
                                <div className={cn(
                                  "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                                  isAssignment ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"
                                )}>
                                  <User className="w-3 h-3" />
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{actorName}</span>
                                {actorEmail && <span className="text-xs text-gray-400">({actorEmail})</span>}
                              </div>
                              <Badge
                                variant={isAssignment ? 'info' : 'warning'}
                                className={cn(
                                  "text-[10px] px-1.5 py-0",
                                  isAssignment && "bg-blue-50 text-blue-700 border-blue-200",
                                  !isAssignment && "bg-amber-50 text-amber-700 border-amber-200"
                                )}
                              >
                                {isAssignment ? 'Asignación' : (comm.status?.label || comm.toStatus || 'Estado')}
                              </Badge>
                              <span className="text-xs text-gray-400 ml-auto">{timeAgo(eventDate)}</span>
                              <ChevronUp className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 ml-1" />
                            </div>

                            {/* Tarjeta de evento */}
                            <div className={cn(
                              'p-3.5 rounded-xl text-sm leading-relaxed border',
                              isAssignment
                                ? 'bg-blue-50/50 border-blue-100 text-gray-800'
                                : 'bg-gray-50 border-gray-200 text-gray-700'
                            )}>
                              <div className="flex items-start gap-2">
                                {isAssignment ? (
                                  <Users className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <History className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="space-y-1">
                                  <p className="font-medium text-gray-900">{comm.reason || `Estado actualizado a ${comm.status?.label || comm.toStatus}`}</p>
                                  {comm.metadata?.assignedToName && (
                                    <p className="text-xs text-blue-700">
                                      Asignado a: <span className="font-semibold">{comm.metadata.assignedToName}</span>
                                      {comm.metadata?.assignerName && <span> por {comm.metadata.assignerName}</span>}
                                      {comm.metadata?.coAssigneeNames?.length > 0 && <span> (Co-asignados: {comm.metadata.coAssigneeNames.join(', ')})</span>}
                                    </p>
                                  )}
                                  {comm.metadata?.previousStatus && (
                                    <p className="text-xs text-gray-500">
                                      Estado anterior: <span className="font-medium">{comm.metadata.previousStatus}</span> → Nuevo: <span className="font-medium">{comm.toStatus}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Fecha completa */}
                            <p className="text-[11px] text-gray-400 mt-1.5">
                              {new Date(eventDate).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      )
                    }

                    const isOutbound = comm.type === 'OUTBOUND'
                    const isInbound = comm.type === 'INBOUND'
                    const isInternal = comm.type === 'INTERNAL_NOTE' || comm.type === 'INTERNAL'
                    const isEmail = isOutbound || isInbound
                    const commDate = comm.date || comm.createdAt || comm.sentAt
                    const authorName = comm.author
                      ? `${comm.author.firstName} ${comm.author.lastName}`
                      : comm.sentByUser
                        ? `${comm.sentByUser.firstName} ${comm.sentByUser.lastName}`
                        : comm.from || comm.sender || 'Sistema'
                    const linkedEmail = comm.emailId ? (c.emails || []).find((e: any) => e.id === comm.emailId) : null
                    const rawRecipients = comm.recipients || comm.to || linkedEmail?.to || []
                    const recipients: string[] = Array.isArray(rawRecipients)
                      ? rawRecipients
                      : (typeof rawRecipients === 'string' && rawRecipients ? rawRecipients.split(/[,;]+/).map((s: string) => s.trim()).filter(Boolean) : [])

                    const rawCc = comm.cc || comm.metadata?.cc || linkedEmail?.cc || []
                    const ccRecipients: string[] = Array.isArray(rawCc)
                      ? rawCc
                      : (typeof rawCc === 'string' && rawCc ? rawCc.split(/[,;]+/).map((s: string) => s.trim()).filter(Boolean) : [])

                    const rawBcc = comm.bcc || comm.metadata?.bcc || linkedEmail?.bcc || []
                    const bccRecipients: string[] = Array.isArray(rawBcc)
                      ? rawBcc
                      : (typeof rawBcc === 'string' && rawBcc ? rawBcc.split(/[,;]+/).map((s: string) => s.trim()).filter(Boolean) : [])
                    const attachments = Array.isArray(comm.attachments) ? comm.attachments : []

                    // Mensaje / Comunicación Plegada (Compacta)
                    if (!isExpanded) {
                      return (
                        <div key={itemKey} className="relative flex gap-4 group">
                          {idx !== communicationList.length - 1 && (
                            <div className="absolute left-[19px] top-8 bottom-0 w-0.5 bg-gray-200" />
                          )}
                          <div className={cn(
                            'relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm',
                            isOutbound ? 'bg-purple-100 text-purple-600' :
                            isInbound ? 'bg-blue-100 text-blue-600' :
                            isInternal ? 'bg-emerald-100 text-emerald-600' :
                            'bg-gray-100 text-gray-500'
                          )}>
                            {isOutbound ? <Send className="w-4 h-4" /> :
                             isInbound ? <Mail className="w-4 h-4" /> :
                             isInternal ? <FileText className="w-4 h-4" /> :
                             <MessageSquare className="w-4 h-4" />}
                          </div>
                          <div className={cn('flex-1 pb-2.5 min-w-0', idx === communicationList.length - 1 ? 'pb-0' : '')}>
                            <div
                              onClick={() => toggleItemExpanded(itemKey)}
                              className={cn(
                                'flex items-center justify-between gap-3 p-3 rounded-xl border transition-all cursor-pointer text-xs',
                                isMostRelevant
                                  ? 'bg-blue-50/40 border-blue-200/90 hover:bg-blue-50/80 shadow-2xs'
                                  : 'bg-white hover:bg-gray-50 border-gray-200/80 hover:border-gray-300'
                              )}
                              title="Haz clic para desplegar mensaje"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-semibold text-gray-900 truncate max-w-[140px] sm:max-w-[200px]">{authorName}</span>
                                <Badge
                                  variant={isOutbound ? 'info' : isInbound ? 'info' : isInternal ? 'success' : 'default'}
                                  className={cn(
                                    "text-[10px] px-1.5 py-0 flex-shrink-0",
                                    isOutbound && "bg-purple-50 text-purple-700 border-purple-200",
                                    isInbound && "bg-blue-50 text-blue-700 border-blue-200"
                                  )}
                                >
                                  {isOutbound ? 'Saliente' : isInbound ? 'Recibido' : isInternal ? 'Nota interna' : (comm.type || '').replace(/_/g, ' ')}
                                </Badge>
                                {isMostRelevant && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 border border-primary-200 flex-shrink-0">
                                    Más relevante
                                  </span>
                                )}
                                <span className="text-gray-500 truncate max-w-md hidden md:inline">
                                  {comm.subject ? `${comm.subject} • ` : ''}
                                  {((comm.content || comm.body || '').replace(/<[^>]+>/g, '').trim()).slice(0, 80)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2.5 flex-shrink-0 text-gray-400">
                                {attachments.length > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                    <Paperclip className="w-3 h-3" /> {attachments.length}
                                  </span>
                                )}
                                <span className="text-[11px]">{timeAgo(commDate)}</span>
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-transform" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    // Mensaje / Comunicación Desplegada Completa
                    return (
                      <div key={itemKey} className="relative flex gap-4 group">
                        {/* Línea de timeline */}
                        {idx !== communicationList.length - 1 && (
                          <div className="absolute left-[19px] top-10 bottom-0 w-0.5 bg-gray-200" />
                        )}

                        {/* Avatar/Icono */}
                        <div className={cn(
                          'relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-sm',
                          isOutbound ? 'bg-purple-100 text-purple-600' :
                          isInbound ? 'bg-blue-100 text-blue-600' :
                          isInternal ? 'bg-emerald-100 text-emerald-600' :
                          'bg-gray-100 text-gray-500'
                        )}>
                          {isOutbound ? <Send className="w-4 h-4" /> :
                           isInbound ? <Mail className="w-4 h-4" /> :
                           isInternal ? <FileText className="w-4 h-4" /> :
                           <MessageSquare className="w-4 h-4" />}
                        </div>

                        {/* Contenido */}
                        <div className={cn(
                          'flex-1 pb-6',
                          idx === communicationList.length - 1 ? 'pb-0' : ''
                        )}>
                          {/* Header: autor + tipo + fecha */}
                          <div
                            onClick={() => toggleItemExpanded(itemKey)}
                            className="flex items-center gap-2 flex-wrap mb-1 cursor-pointer select-none hover:opacity-80 transition-opacity"
                            title="Haz clic para plegar mensaje"
                          >
                            <div className="flex items-center gap-1.5">
                              <div className={cn(
                                "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                                isOutbound ? "bg-purple-100 text-purple-600" :
                                isInbound ? "bg-blue-100 text-blue-600" :
                                "bg-emerald-100 text-emerald-600"
                              )}>
                                <User className="w-3 h-3" />
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{authorName}</span>
                            </div>
                            <Badge
                              variant={isOutbound ? 'info' : isInbound ? 'info' : isInternal ? 'success' : 'default'}
                              className={cn(
                                "text-[10px] px-1.5 py-0",
                                isOutbound && "bg-purple-50 text-purple-700 border-purple-200",
                                isInbound && "bg-blue-50 text-blue-700 border-blue-200"
                              )}
                            >
                              {isOutbound ? 'Saliente' : isInbound ? 'Recibido' : isInternal ? 'Nota interna' : (comm.type || '').replace(/_/g, ' ')}
                            </Badge>
                            {isMostRelevant && (
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200">
                                Más relevante
                              </span>
                            )}
                            <span className="text-xs text-gray-400 ml-auto">{timeAgo(commDate)}</span>
                            <ChevronUp className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 ml-1" />
                          </div>

                          {/* Subject */}
                          {comm.subject && (
                            <p className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                              <span className="text-gray-400">Asunto:</span> {comm.subject}
                            </p>
                          )}

                          {/* Destinatarios (Para) */}
                          {recipients.length > 0 && (
                            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                              <AtSign className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-[11px] font-medium text-gray-500">Para:</span>
                              {recipients.map((r: string, ri: number) => (
                                <span key={ri} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[11px] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                  {r}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Copiados (CC) */}
                          {ccRecipients.length > 0 && (
                            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                              <span className="text-[11px] font-medium text-gray-500 pl-4">CC:</span>
                              {ccRecipients.map((c: string, ci: number) => (
                                <span key={ci} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50/60 dark:bg-blue-950/40 text-[11px] text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Copia Oculta (CCO) */}
                          {bccRecipients.length > 0 && (
                            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                              <span className="text-[11px] font-medium text-gray-500 pl-4">CCO:</span>
                              {bccRecipients.map((b: string, bi: number) => (
                                <span key={bi} className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50/60 dark:bg-purple-950/40 text-[11px] text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                                  {b}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Cuerpo del mensaje */}
                          <div className={cn(
                            'p-3.5 rounded-xl text-sm leading-relaxed',
                            isInternal
                              ? 'bg-emerald-50/60 border border-emerald-100 text-gray-700'
                              : isOutbound
                                ? 'bg-purple-50/40 border border-purple-100 text-gray-700'
                                : isInbound
                                  ? 'bg-blue-50/40 border border-blue-100 text-gray-700'
                                  : 'bg-gray-50 border border-gray-100 text-gray-700'
                          )}>
                            <QuotedTextCollapsible
                              content={comm.content || comm.body || ''}
                              htmlContent={comm.htmlBody || (comm.content && isHtml(comm.content) ? comm.content : undefined)}
                            />
                          </div>

                          {/* Adjuntos */}
                          {attachments.length > 0 && (
                            <div className="mt-2.5 space-y-1">
                              <p className="text-[11px] font-medium text-gray-400 flex items-center gap-1">
                                <Paperclip className="w-3 h-3" />
                                Adjuntos ({attachments.length})
                              </p>
                              {attachments.map((att: any, ai: number) => {
                                const attUrl = getAssetUrl(att.url)
                                return (
                                <a key={att.id || ai} href={attUrl} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50/30 transition-colors group/att">
                                  <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover/att:bg-primary-100 transition-colors">
                                    {att.mimeType?.startsWith('image/') ? (
                                      <img src={attUrl} alt={att.filename} className="w-7 h-7 object-cover rounded-md" />
                                    ) : (
                                      <FileText className="w-3.5 h-3.5 text-gray-500 group-hover/att:text-primary-600" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-700 truncate group-hover/att:text-primary-700">{att.filename}</p>
                                    {typeof att.size === 'number' && (
                                      <p className="text-[10px] text-gray-400">{Math.round(att.size / 1024)} KB</p>
                                    )}
                                  </div>
                                </a>
                                )
                              })}
                            </div>
                          )}

                          {/* Fecha completa */}
                          <p className="text-[11px] text-gray-400 mt-1.5">
                            {new Date(commDate).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No hay comunicaciones registradas</p>
                </div>
              )}
            </div>
          </Card>

      {/* MODALES */}
      {showAssignModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setShowAssignModal(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Asignar caso</h3>
                <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Responsable principal</p>
                <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 rounded-xl p-2">
                  {[...references?.users || []]
                    .sort((a: any, b: any) => {
                      if (a.id === c.assignee?.id) return -1
                      if (b.id === c.assignee?.id) return 1
                      if (a.id === selectedAssignee) return -1
                      if (b.id === selectedAssignee) return 1
                      return 0
                    })
                    .map((user: any) => {
                      const isCurrentAssignee = c.assignee?.id === user.id
                      const isSelected = selectedAssignee === user.id
                      return (
                        <label key={user.id} className={cn(
                          'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg cursor-pointer transition-all',
                          isSelected ? 'bg-primary-50 border border-primary-200 shadow-sm' : 'hover:bg-gray-50 border border-transparent'
                        )}>
                          <input type="radio" name="primaryAssignee" value={user.id} checked={isSelected} onChange={() => setSelectedAssignee(user.id)} className="text-primary-600 focus:ring-primary-500" />
                          <div className={cn(
                            "flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0",
                            isSelected ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"
                          )}>
                            <User className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={cn("text-sm font-medium", isSelected ? "text-primary-900" : "text-gray-900")}>{user.firstName} {user.lastName}</p>
                              {isCurrentAssignee && <span className="text-[10px] font-semibold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">Actual</span>}
                            </div>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                          </div>
                        </label>
                      )
                    })}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Co-asignados <span className="text-gray-400 font-normal">({selectedCoAssignees.size} seleccionados)</span></p>
                <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-200 rounded-xl p-2">
                  {[...references?.users || []]
                    .filter((u: any) => u.id !== selectedAssignee)
                    .sort((a: any, b: any) => {
                      const aSelected = selectedCoAssignees.has(a.id)
                      const bSelected = selectedCoAssignees.has(b.id)
                      if (aSelected && !bSelected) return -1
                      if (!aSelected && bSelected) return 1
                      return 0
                    })
                    .map((user: any) => {
                      const isSelected = selectedCoAssignees.has(user.id)
                      return (
                        <label key={user.id} className={cn(
                          'flex items-center gap-3 w-full px-3 py-2 rounded-lg cursor-pointer transition-all',
                          isSelected ? 'bg-primary-50 border border-primary-100' : 'hover:bg-gray-50 border border-transparent'
                        )}>
                          <input type="checkbox" checked={isSelected} onChange={(e) => {
                            const next = new Set(selectedCoAssignees)
                            if (e.target.checked) next.add(user.id)
                            else next.delete(user.id)
                            setSelectedCoAssignees(next)
                          }} className="text-primary-600 focus:ring-primary-500 rounded" />
                          <div className={cn(
                            "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
                            isSelected ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"
                          )}>
                            <User className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm font-medium", isSelected ? "text-primary-900" : "text-gray-700")}>{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                          </div>
                        </label>
                      )
                    })}
                  {references?.users?.filter((u: any) => u.id !== selectedAssignee).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-3">Seleccione un responsable principal primero</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowAssignModal(false)}>Cancelar</Button>
                <Button size="sm" onClick={handleAssign} disabled={!selectedAssignee || assignCase.isPending}>
                  {assignCase.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar asignación'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Alertas */}
      {showAlertsModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setShowAlertsModal(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[70vh] flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Alertas ({(c.alerts || []).length})
                </h3>
                <button onClick={() => setShowAlertsModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {(c.alerts || []).map((alert: any) => {
                  const alertTypeLabel = typeof alert.type === 'object'
                    ? (alert.type?.label || alert.type?.name || 'Alerta')
                    : (alert.type ? String(alert.type) : 'Alerta')
                  const alertSeverityLabel = typeof alert.severity === 'object'
                    ? (alert.severity?.label || alert.severity?.name || 'Severidad')
                    : (alert.severity ? String(alert.severity) : 'Severidad')

                  return (
                    <div key={alert.id} className="flex items-start gap-3 p-4 rounded-xl bg-red-50/60 border border-red-100">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{alert.title || 'Alerta'}</p>
                        <p className="text-xs text-gray-500 mt-1">{alertTypeLabel}</p>
                        {alert.description && <p className="text-xs text-gray-600 mt-1">{alert.description}</p>}
                      </div>
                      <Badge variant="danger" className="text-[10px] flex-shrink-0">{alertSeverityLabel}</Badge>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Co-asignados */}
      {showCoAssigneesModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setShowCoAssigneesModal(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md max-h-[60vh] flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  Co-asignados ({(c as any).coAssignees?.length || 0})
                </h3>
                <button onClick={() => setShowCoAssigneesModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                {((c as any).coAssignees?.length ?? 0) > 0 ? (
                  <div className="space-y-2">
                    {(c as any).coAssignees.map((ca: any) => {
                      const initials = (ca.firstName || ca.lastName)
                        ? `${(ca.firstName || '')[0] || ''}${(ca.lastName || '')[0] || ''}`.toUpperCase()
                        : (ca.email?.[0]?.toUpperCase() || 'U')
                      return (
                        <div key={ca.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-600">{initials}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {ca.firstName || ca.lastName ? `${ca.firstName || ''} ${ca.lastName || ''}`.trim() : (ca.email || 'Sin nombre')}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{ca.email || ''}</p>
                            {ca.area && (
                              <p className="text-xs text-gray-400 mt-0.5">{ca.area.name}</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No hay co-asignados</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Emails */}
      {showEmailsModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setShowEmailsModal(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[70vh] flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Emails ({(c.emails || []).length})
                </h3>
                <button onClick={() => setShowEmailsModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {(c.emails || []).map((email: any) => (
                  <button
                    key={email.id}
                    onClick={() => { navigate(`/emails/${email.id}`); setShowEmailsModal(false); }}
                    className="w-full flex items-start gap-3 p-4 rounded-xl bg-blue-50/40 border border-blue-100 hover:bg-blue-50 transition-colors text-left group"
                  >
                    <Mail className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700 transition-colors">{email.subject || '(Sin asunto)'}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-gray-500">{email.from}</span>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-400">{timeAgo(email.receivedAt)}</span>
                      </div>
                    </div>
                    <Link2 className="w-3.5 h-3.5 text-blue-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showEscalateModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setShowEscalateModal(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Escalar Caso — Nivel {escalateLevel}
                </h3>
                <button onClick={() => setShowEscalateModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nivel de escalamiento</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { level: 1, label: 'Nivel 1', desc: 'Supervisor', color: 'bg-amber-50 border-amber-200 text-amber-700 ring-amber-300' },
                    { level: 2, label: 'Nivel 2', desc: 'Manager', color: 'bg-orange-50 border-orange-200 text-orange-700 ring-orange-300' },
                    { level: 3, label: 'Nivel 3', desc: 'Director', color: 'bg-red-50 border-red-200 text-red-700 ring-red-300' },
                    { level: 4, label: 'Nivel 4', desc: 'Ejecutivo', color: 'bg-rose-50 border-rose-200 text-rose-700 ring-rose-300' },
                  ].map((l) => (
                    <button key={l.level} onClick={() => setEscalateLevel(l.level)}
                      className={cn(
                        'p-2.5 rounded-xl border text-center transition-all',
                        escalateLevel === l.level ? cn(l.color, 'ring-2 ring-offset-1') : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                      )}>
                      <p className="text-xs font-bold">{l.label}</p>
                      <p className="text-[10px] mt-0.5">{l.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Categoría</label>
                <select value={escalateCategory} onChange={(e) => setEscalateCategory(e.target.value)}
                  className="block w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500">
                  <option value="operational">Operacional</option>
                  <option value="legal">Legal / Jurídico</option>
                  <option value="sla_breach">Incumplimiento SLA</option>
                  <option value="critical_risk">Riesgo Crítico</option>
                  <option value="client_complaint">Reclamo del Cliente</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Notificar a (área)</label>
                <select value={escalateAreaId} onChange={(e) => setEscalateAreaId(e.target.value)}
                  className="block w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500">
                  <option value="">Seleccionar área...</option>
                  {(areas || []).map((area: any) => (
                    <option key={area.id} value={area.id}>{area.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Motivo de escalación</label>
                <textarea value={escalateReason} onChange={(e) => setEscalateReason(e.target.value)} placeholder="Describa el motivo detallado..." rows={3}
                  className="block w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 resize-none" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowEscalateModal(false)}>Cancelar</Button>
                <Button variant="danger" size="sm" onClick={handleEscalate} disabled={escalateCase.isPending || !escalateReason.trim()}>
                  {escalateCase.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                  Escalar a Nivel {escalateLevel}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ MODAL DE ANÁLISIS INTEGRAL DEL HISTORIAL CON IA ═══════════ */}
      {showAnalysisModal && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" onClick={() => setShowAnalysisModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl border border-purple-100 w-full max-w-3xl p-6 sm:p-8 space-y-6 pointer-events-auto max-h-[90vh] overflow-y-auto">
              
              {/* Header */}
              <div className="flex items-start justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-md">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      Auditoría y Reporte Inteligente del Historial
                    </h3>
                    <p className="text-xs text-gray-500">
                      Caso #{c.caseNumber || c.id} — Evaluado en tiempo real con Inteligencia Artificial
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnalysisModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Contenido del Análisis */}
              {analyzeCaseHistoryMutation.isPending ? (
                <div className="py-16 flex flex-col items-center justify-center space-y-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-gray-900">Analizando todo el historial del caso...</h4>
                    <p className="text-xs text-gray-500 max-w-sm">
                      Auditando correos vinculados, comunicaciones, notas del equipo, estados y cumplimiento de SLAs.
                    </p>
                  </div>
                </div>
              ) : analysisResult ? (
                <div className="space-y-6 text-gray-800">
                  
                  {/* Resumen Ejecutivo */}
                  <div className="p-4 bg-purple-50/60 rounded-xl border border-purple-100 space-y-2">
                    <div className="flex items-center gap-2 text-purple-900 font-bold text-xs uppercase tracking-wider">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span>Resumen Ejecutivo</span>
                    </div>
                    <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                      {analysisResult.executiveSummary}
                    </p>
                  </div>

                  {/* Cuadrícula: Qué se hizo vs Qué falta */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* QUÉ SE HIZO */}
                    <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-100 space-y-3">
                      <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs uppercase tracking-wider">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>¿Qué se hizo? (Acciones Realizadas)</span>
                      </div>
                      <ul className="space-y-2">
                        {(analysisResult.whatWasDone || []).map((item: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* QUÉ NO SE HIZO / PENDIENTES */}
                    <div className="p-4 bg-amber-50/60 rounded-xl border border-amber-100 space-y-3">
                      <div className="flex items-center gap-2 text-amber-900 font-bold text-xs uppercase tracking-wider">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span>¿Qué falta por hacer? (Pendientes Críticos)</span>
                      </div>
                      <ul className="space-y-2">
                        {(analysisResult.whatIsPending || []).map((item: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>

                  {/* Evaluación de Riesgos y Recomendaciones */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* RIESGOS / ALERTAS */}
                    <div className="p-4 bg-rose-50/60 rounded-xl border border-rose-100 space-y-2">
                      <div className="flex items-center gap-2 text-rose-900 font-bold text-xs uppercase tracking-wider">
                        <AlertTriangle className="w-4 h-4 text-rose-600" />
                        <span>Riesgos y Alertas Detectadas</span>
                      </div>
                      {analysisResult.risksAndAlerts?.length > 0 ? (
                        <ul className="space-y-1.5">
                          {analysisResult.risksAndAlerts.map((risk: string, idx: number) => (
                            <li key={idx} className="text-xs text-rose-800 flex items-start gap-1.5">
                              <span className="shrink-0">•</span>
                              <span>{risk}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500 italic">No se detectaron riesgos de severidad crítica.</p>
                      )}
                    </div>

                    {/* PRÓXIMOS PASOS */}
                    <div className="p-4 bg-blue-50/60 rounded-xl border border-blue-100 space-y-2">
                      <div className="flex items-center gap-2 text-blue-900 font-bold text-xs uppercase tracking-wider">
                        <ArrowRight className="w-4 h-4 text-blue-600" />
                        <span>Próximas Acciones Sugeridas</span>
                      </div>
                      <ul className="space-y-1.5">
                        {(analysisResult.recommendedActions || []).map((act: string, idx: number) => (
                          <li key={idx} className="text-xs text-blue-900 flex items-start gap-1.5">
                            <span className="shrink-0">•</span>
                            <span>{act}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>

                  {/* Evaluación de SLA */}
                  {analysisResult.slaStatusAssessment && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                      <span className="font-semibold text-gray-700">Estado del SLA:</span>
                      <Badge variant={analysisResult.slaStatusAssessment.isAtRisk ? 'danger' : 'success'} className="px-2 py-0.5">
                        {analysisResult.slaStatusAssessment.isAtRisk ? 'En Riesgo de Incumplimiento' : 'En Tiempos Estándar'}
                      </Badge>
                      <span className="text-gray-500">{analysisResult.slaStatusAssessment.explanation}</span>
                    </div>
                  )}

                  {/* Barra de Acciones del Reporte */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const res = await analyzeCaseHistoryMutation.mutateAsync({ caseId: id! })
                          setAnalysisResult(res)
                          successToast('Análisis re-evaluado con éxito')
                        } catch (err: any) {
                          errorToast('Error actualizando análisis')
                        }
                      }}
                      disabled={analyzeCaseHistoryMutation.isPending}
                    >
                      <RefreshCw className={cn("w-3.5 h-3.5 mr-1.5", analyzeCaseHistoryMutation.isPending && "animate-spin")} />
                      Re-analizar
                    </Button>

                    <div className="flex items-center gap-2">
                      {/* Copiar Reporte Completo */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reportText = `========================================================\nREPORTE DE AUDITORÍA Y HISTORIAL DE CASO\nCaso #${c.caseNumber || c.id} — ${c.subject}\nÁrea: ${c.area?.name || 'Servicio al Cliente'}\nFecha de generación: ${new Date().toLocaleString('es-CO')}\n========================================================\n\n1. RESUMEN EJECUTIVO:\n${analysisResult.executiveSummary}\n\n2. ¿QUÉ SE HIZO?:\n${(analysisResult.whatWasDone || []).map((w: string) => `• ${w}`).join('\n')}\n\n3. ¿QUÉ FALTA POR HACER?:\n${(analysisResult.whatIsPending || []).map((p: string) => `• ${p}`).join('\n')}\n\n4. RIESGOS:\n${(analysisResult.risksAndAlerts || []).map((r: string) => `• ${r}`).join('\n') || 'Ninguno detectado'}\n\n5. PRÓXIMAS ACCIONES RECOMENDADAS:\n${(analysisResult.recommendedActions || []).map((a: string) => `• ${a}`).join('\n')}\n\nEvaluación SLA: ${analysisResult.slaStatusAssessment?.explanation || 'N/A'}\n========================================================`
                          navigator.clipboard.writeText(reportText)
                          setCopiedReport(true)
                          successToast('Reporte completo copiado al portapapeles')
                          setTimeout(() => setCopiedReport(false), 2500)
                        }}
                      >
                        {copiedReport ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                        {copiedReport ? '¡Copiado!' : 'Copiar Reporte'}
                      </Button>

                      {/* Descargar Reporte (.doc / .txt) */}
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          const reportText = `========================================================\nREPORTE FORMAL DE AUDITORÍA Y GESTIÓN DE CASO\nCaso #${c.caseNumber || c.id} — ${c.subject}\nÁrea: ${c.area?.name || 'Servicio al Cliente'}\nResponsable: ${c.assignee ? `${c.assignee.firstName} ${c.assignee.lastName}` : 'Sin asignar'}\nFecha de emisión: ${new Date().toLocaleString('es-CO')}\nEmpresa: HUMAN TALENT S.A.S.\n========================================================\n\n1. RESUMEN EJECUTIVO\n--------------------------------------------------------\n${analysisResult.executiveSummary}\n\n2. GESTIONES Y ACCIONES REALIZADAS\n--------------------------------------------------------\n${(analysisResult.whatWasDone || []).map((w: string) => `[✓] ${w}`).join('\n')}\n\n3. PENDIENTES Y REQUERIMIENTOS EN CURSO\n--------------------------------------------------------\n${(analysisResult.whatIsPending || []).map((p: string) => `[⏳] ${p}`).join('\n')}\n\n4. RIESGOS Y EVALUACIÓN DE SLA\n--------------------------------------------------------\n${(analysisResult.risksAndAlerts || []).map((r: string) => `[!] ${r}`).join('\n') || 'Sin riesgos críticos identificados'}\n\nSLA: ${analysisResult.slaStatusAssessment?.explanation || 'Operación en tiempos normales'}\n\n5. PRÓXIMAS ACCIONES SUGERIDAS\n--------------------------------------------------------\n${(analysisResult.recommendedActions || []).map((a: string) => `[→] ${a}`).join('\n')}\n\n========================================================\nDocumento generado automáticamente por ComCenter AI Torre de Control\nHUMAN TALENT S.A.S.`
                          
                          const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' })
                          const url = URL.createObjectURL(blob)
                          const link = document.createElement('a')
                          link.href = url
                          link.download = `Reporte_Caso_${c.caseNumber || c.id}_${new Date().toISOString().slice(0, 10)}.txt`
                          document.body.appendChild(link)
                          link.click()
                          document.body.removeChild(link)
                          URL.revokeObjectURL(url)
                          successToast('Documento de reporte descargado')
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        Descargar Documento
                      </Button>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>Presione "Analizar historial" para generar el informe.</p>
                </div>
              )}

            </div>
          </div>
        </>
      )}

      </div>
    </ErrorBoundary>
  )
}

